/* eslint-disable import/no-extraneous-dependencies */
import React, { ChangeEvent, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from 'utils/hooks';

import { countBy } from 'lodash';

import Tippy from '@tippyjs/react';

import multiphotos from 'domains/multiphotos';
import cartModule from 'domains/cart';
import products from 'domains/products';
import optionGroups from 'domains/option_groups';
import ui from 'domains/ui';
import activeEditor from 'domains/active_editor';
import canvases from 'domains/canvases';
import cartProductCreator from 'domains/cart_product_creator';

import Button from 'components/shared/Button';
import MultiCanvasAddToCart from './MultiCanvasAddToCart';
import MultiPhotoAddToCart from './MultiPhotoAddToCart';
import StandardAddToCart from './StandardAddToCart';
import { hasFullyConfiguredCanvas, notNull } from 'utils/type_guards';
import { hasAllRequiredOptionGroups } from 'utils/option_util';
import {
  selectCustomPriceError,
  selectCustomPriceLoading,
  selectOptionsLoading,
  selectSubcatalogsLoading,
} from 'selectors/loading_states';

export type SharedAddToCartProps = {
  quantity: number;
  disable: boolean;
};

function AddToCartWrapper() {
  const dispatch = useAppDispatch();
  const activeProduct = useAppSelector(activeEditor.selectors.product);
  const allCanvasProductIds = useAppSelector(canvases.selectors.allProductIds);
  const productsById = useAppSelector(products.selectors.selectEntities);
  const multiPhotoIds = useAppSelector(multiphotos.selectors.selectIds);
  const hasMobileNavigation = useAppSelector(ui.selectors.hasMobileNavigation);
  const addToCartQuantity = useAppSelector(cartProductCreator.selectors.quantity);

  const status = useAppSelector(cartModule.selectors.status);
  const isProcessing = useAppSelector(cartProductCreator.selectors.isProcessing);
  const canvasesLocked = useAppSelector(canvases.selectors.locked);
  const subcatalogsLoading = useAppSelector(selectSubcatalogsLoading);
  const optionsLoading = useAppSelector(selectOptionsLoading);
  const customPriceLoading = useAppSelector(selectCustomPriceLoading);
  const hasCustomPriceError = useAppSelector(selectCustomPriceError);

  const allCanvases = useAppSelector(canvases.selectors.selectAll);
  const allOptionGroupsById = useAppSelector(optionGroups.selectors.selectEntities);
  const allOptionGroupsByTemplateId = useAppSelector(optionGroups.selectors.allByTemplateId);

  const allCanvasesReady = useMemo(() => {
    return (
      allCanvases.length > 0 &&
      allCanvases.every((c) => {
        if (!hasFullyConfiguredCanvas(c)) return false;

        const optionGroupIds = allOptionGroupsByTemplateId[c.product];
        if (!optionGroupIds) return false;

        const optionGroups = optionGroupIds.map((id) => allOptionGroupsById[id]).filter(notNull);
        return hasAllRequiredOptionGroups(c.options, optionGroups);
      })
    );
  }, [allCanvases, allOptionGroupsById, allOptionGroupsByTemplateId]);

  const hasCartErrorStatus = useMemo(() => status === 'error', [status]);

  const disable = useMemo(
    () =>
      isProcessing ||
      canvasesLocked ||
      subcatalogsLoading ||
      optionsLoading ||
      customPriceLoading ||
      hasCustomPriceError ||
      !allCanvasesReady ||
      addToCartQuantity < 1,
    [
      allCanvasesReady,
      addToCartQuantity,
      canvasesLocked,
      isProcessing,
      subcatalogsLoading,
      optionsLoading,
      customPriceLoading,
      hasCustomPriceError,
    ]
  );

  const showMultiPhotosButton = useMemo(() => {
    return !hasCartErrorStatus && multiPhotoIds.length > 0;
  }, [multiPhotoIds, hasCartErrorStatus]);

  const showMultiCanvasButton = useMemo(() => {
    return !hasCartErrorStatus && multiPhotoIds.length === 0 && allCanvases.length > 1;
  }, [multiPhotoIds, allCanvases, hasCartErrorStatus]);

  const showStandardButton = useMemo(() => {
    return !hasCartErrorStatus && multiPhotoIds.length === 0 && allCanvases.length === 1;
  }, [multiPhotoIds, allCanvases, hasCartErrorStatus]);

  const multiCanvasProductNames = useMemo(() => {
    const productNames = allCanvasProductIds.map((id) => {
      return id && !!productsById[id] ? productsById[id]?.name_long : '';
    });

    const countedNames = countBy(productNames);

    return Object.keys(countedNames).map((name) => {
      const count = countedNames[name] === 1 ? '' : `(${countedNames[name]})`;
      return (
        <p key={name}>
          {name} {count}
        </p>
      );
    });
  }, [allCanvasProductIds, productsById]);

  const productDescription = useMemo(
    () => (activeProduct ? activeProduct.name_long : ''),
    [activeProduct]
  );

  function setQuantity(e: ChangeEvent<HTMLInputElement>) {
    const { target } = e;
    dispatch(cartProductCreator.actions.updateQuantity(target.value));
  }

  return (
    <div className="add_to_cart">
      <div className="cart_button_wrap">
        <div className="quantity_select">
          <div className="quantity_select_inner">
            <label htmlFor="quantity_select" className={`${addToCartQuantity < 1 ? 'error' : ''}`}>
              Qty
            </label>
            <input
              type="number"
              min="1"
              value={addToCartQuantity}
              id="quantity_select"
              required
              onChange={setQuantity}
            />
          </div>
        </div>
        {showMultiCanvasButton && (
          <div className="cart_button_product_desc">
            <Tippy content={multiCanvasProductNames} theme="light">
              <span>Custom Arrangement</span>
            </Tippy>
          </div>
        )}
        {!showMultiCanvasButton && !hasMobileNavigation && (
          <div
            className="cart_button_product_desc"
            dangerouslySetInnerHTML={{ __html: productDescription }}
          />
        )}

        {!showMultiCanvasButton && hasMobileNavigation && (
          // Label is contained in the dangerous html
          // eslint-disable-next-line jsx-a11y/control-has-associated-label
          <button
            className="mobile-cart-description cart_button_product_desc"
            onClick={() => dispatch(ui.actions.setShowMobileSidebar(true))}
            dangerouslySetInnerHTML={{ __html: productDescription }}
          />
        )}
      </div>
      <div className="cart_button_wrap">
        {hasCartErrorStatus && (
          <Button
            type="cornered"
            className="cart_button error"
            id="add-to-cart-button"
            onClick={() => {}}
            disabled
          >
            Unavailable
          </Button>
        )}
        {showMultiPhotosButton && (
          <MultiPhotoAddToCart disable={disable} quantity={addToCartQuantity} />
        )}
        {showMultiCanvasButton && (
          <MultiCanvasAddToCart disable={disable} quantity={addToCartQuantity} />
        )}
        {showStandardButton && <StandardAddToCart disable={disable} quantity={addToCartQuantity} />}
      </div>
    </div>
  );
}

export default AddToCartWrapper;
